import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlannerStep1Goal } from './PlannerStep1Goal';
import { PlannerStep2Brief } from './PlannerStep2Brief';
import { PlannerStep3Assets } from './PlannerStep3Assets';
import { useAuth } from '@/context/AuthContext';
import { useCreateCampaignPlan, useUpdateCampaignPlan, useCampaignPlan } from '@/hooks/useCampaignPlanner';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CampaignPlannerWizardProps {
  open: boolean;
  onClose: () => void;
  editingPlanId?: string;
}

export const CampaignPlannerWizard: React.FC<CampaignPlannerWizardProps> = ({ open, onClose, editingPlanId }) => {
  const { profile } = useAuth();
  const createPlan = useCreateCampaignPlan();
  const updatePlan = useUpdateCampaignPlan();
  const { data: existingPlan } = useCampaignPlan(editingPlanId);

  const [step, setStep] = useState(editingPlanId && existingPlan?.strategic_brief ? 2 : 0);
  const [planId, setPlanId] = useState<string | undefined>(editingPlanId);
  const [idea, setIdea] = useState('');
  const [goal, setGoal] = useState('custom');
  const [name, setName] = useState('');
  const [strategicBrief, setStrategicBrief] = useState<any>(null);
  const [tone, setTone] = useState('professional');

  // Sync existing plan data
  React.useEffect(() => {
    if (existingPlan) {
      setIdea(existingPlan.idea_prompt || '');
      setGoal(existingPlan.goal || 'custom');
      setName(existingPlan.name || '');
      setStrategicBrief(existingPlan.strategic_brief);
      setTone(existingPlan.tone || 'professional');
      if (existingPlan.strategic_brief) setStep(2);
    }
  }, [existingPlan]);

  const handleStep1Complete = async (data: { idea: string; goal: string; name: string }) => {
    setIdea(data.idea);
    setGoal(data.goal);
    setName(data.name);

    try {
      if (!planId) {
        const plan = await createPlan.mutateAsync({
          company_id: profile?.company_id,
          name: data.name || `Campaign - ${data.goal}`,
          goal: data.goal,
          idea_prompt: data.idea,
          created_by: profile?.id,
          status: 'draft',
          tone: 'professional',
        });
        setPlanId(plan.id);
      } else {
        await updatePlan.mutateAsync({
          id: planId,
          updates: { idea_prompt: data.idea, goal: data.goal, name: data.name },
        });
      }
      setStep(1);
    } catch (e) {
      toast.error('Failed to save campaign plan');
    }
  };

  const handleBriefGenerated = async (brief: any) => {
    setStrategicBrief(brief);
    if (planId) {
      await updatePlan.mutateAsync({ id: planId, updates: { strategic_brief: brief } });
    }
    setStep(2);
  };

  const steps = ['Goal & Idea', 'Strategic Brief', 'Assets & Review'];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Campaign Planner
          </DialogTitle>
          {/* Stepper */}
          <div className="flex items-center gap-2 pt-2">
            {steps.map((label, i) => (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-2 text-sm ${i <= step ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${i < step ? 'bg-primary text-primary-foreground' : i === step ? 'border-2 border-primary text-primary' : 'border border-muted-foreground/30 text-muted-foreground'}`}>
                    {i + 1}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-border" />}
              </React.Fragment>
            ))}
          </div>
        </DialogHeader>

        <div className="mt-4">
          {step === 0 && (
            <PlannerStep1Goal
              initialIdea={idea}
              initialGoal={goal}
              initialName={name}
              onNext={handleStep1Complete}
              isLoading={createPlan.isPending}
            />
          )}
          {step === 1 && planId && (
            <PlannerStep2Brief
              idea={idea}
              goal={goal}
              companyId={profile?.company_id || ''}
              onBriefGenerated={handleBriefGenerated}
              onBack={() => setStep(0)}
              existingBrief={strategicBrief}
            />
          )}
          {step === 2 && planId && strategicBrief && (
            <PlannerStep3Assets
              planId={planId}
              strategicBrief={strategicBrief}
              tone={tone}
              onToneChange={setTone}
              companyId={profile?.company_id || ''}
              onBack={() => setStep(1)}
              onDone={onClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
